import React, { useEffect, useState } from 'react'
import styled from 'styled-components'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@tanstack/react-query'
import { FormProvider, useForm, useWatch } from 'react-hook-form'
import { useBusinessContext } from '@context'
import { AnimatedContentLoader, ModalV2, Button } from '@components'
import { NocfoPlan } from '@constants'
import {
  createCheckoutSession,
  IPricingPlan,
  pricingPlansQueryKey,
  usePaymentMethodStatus,
  usePricingPlans
} from '@query'
import toast from 'react-hot-toast'
import { getApiErrorMessage, setAPIErrors } from '@utils'
import { useQueryClient } from '@tanstack/react-query'
import { BillingSetupPanel } from '@root/containers/Billing/BillingSetupPanel'

import { PlanSelectionStep } from './PlanSelectionStep'
import { OrderReviewStep } from './OrderReviewStep'

type BillingInterval = 'month' | 'year'
export type PricingTableStep = 'plans' | 'review' | 'payment'

interface CustomPricingTableFormValues {
  interval: BillingInterval
  selectedPlanId: string
  step: PricingTableStep
  promotion_code: string
}

const toNocfoPlan = (planId: string | undefined): NocfoPlan =>
  planId?.toUpperCase() === NocfoPlan.PRO ? NocfoPlan.PRO : NocfoPlan.PLUS

const MODAL_SIZE_BY_STEP: Record<PricingTableStep, { width: number; height: number }> = {
  plans: { width: 1000, height: 700 },
  review: { width: 600, height: 700 },
  payment: { width: 600, height: 900 }
}

export const CustomPricingTable: React.FC<{
  onUpgradeComplete?: () => void
  onStepChange?: (step: PricingTableStep) => void
}> = ({ onUpgradeComplete, onStepChange }) => {
  const {
    slug,
    data: { stripe_payg_checkout_enabled: stripePaygCheckoutEnabled }
  } = useBusinessContext()
  const [t] = useTranslation()
  const queryClient = useQueryClient()

  const { data, isLoading } = usePricingPlans(slug)
  const { data: paymentMethodStatus, isLoading: isLoadingPaymentMethod } = usePaymentMethodStatus({
    slug,
    enabled: stripePaygCheckoutEnabled
  })
  const hasDefaultPaymentMethod = paymentMethodStatus?.has_payment_method ?? false
  const form = useForm<CustomPricingTableFormValues>({
    defaultValues: {
      interval: 'month',
      selectedPlanId: '',
      step: 'plans',
      promotion_code: ''
    }
  })
  const { control, setValue, setError } = form
  const { interval, selectedPlanId, step, promotion_code: promotionCode } = useWatch({ control })
  const reviewPlan = data?.plans.find(plan => plan.id === selectedPlanId) ?? null

  const { mutate: checkout, isPending: isCheckingOut } = useMutation({
    mutationFn: ({
      plan,
      billingInterval,
      promotionCode: code
    }: {
      plan: IPricingPlan
      billingInterval: BillingInterval
      promotionCode: string
    }) => {
      const price_id = billingInterval === 'year' ? plan?.yearly_price_id : plan?.monthly_price_id
      if (!price_id) {
        throw new Error(t('general.error'))
      }
      return createCheckoutSession(
        { slug },
        {
          price_id,
          promotion_code: code
        }
      )
    },
    onSuccess: async result => {
      if (result.completed) {
        await Promise.all([
          queryClient.invalidateQueries({ queryKey: [slug] }),
          queryClient.invalidateQueries({ queryKey: ['businesses'] }),
          queryClient.invalidateQueries({ queryKey: pricingPlansQueryKey(slug) })
        ])
        toast.success(t('stripeConfirmation.success'), { icon: '🎉', duration: 10000 })
        onUpgradeComplete?.()
        return
      }

      if (!result.checkout_url) {
        toast.error(t('general.error'))
        return
      }
      window.location.href = result.checkout_url
    },
    onError: error => {
      const { status, data } = (error as any) ?? {}
      if (status === 400) {
        setAPIErrors(data, setError)
      } else {
        toast.error(getApiErrorMessage(error, t('general.error')))
      }
    }
  })

  const goToStep = (nextStep: PricingTableStep) => {
    setValue('step', nextStep, { shouldDirty: true })
    onStepChange?.(nextStep)
  }

  const handleSelectPlan = (plan: IPricingPlan) => {
    setValue('selectedPlanId', plan.id, { shouldDirty: true })
    goToStep('review')
  }

  const handleContinueFromReview = () => {
    if (hasDefaultPaymentMethod) {
      handleStripeCheckout()
      return
    }
    goToStep('payment')
  }

  const handleStripeCheckout = () => {
    if (!reviewPlan) {
      return
    }
    checkout({
      plan: reviewPlan,
      billingInterval: interval,
      promotionCode: promotionCode ?? ''
    })
  }

  const handleBackToPlans = () => {
    goToStep('plans')
  }

  const handleBackToReview = () => {
    goToStep('review')
  }

  return (
    <FormProvider {...form}>
      <AnimatedContentLoader isLoading={isLoading}>
        <Content>
          {data && reviewPlan && step === 'review' ? (
            <OrderReviewStep
              plan={reviewPlan}
              interval={interval ?? 'month'}
              trialAvailable={data.trial_available}
              hasPaymentMethod={hasDefaultPaymentMethod}
              isContinueDisabled={isLoadingPaymentMethod}
              isSubmitting={isCheckingOut}
              onBack={handleBackToPlans}
              onContinue={handleContinueFromReview}
            />
          ) : data && reviewPlan && step === 'payment' ? (
            <PaymentStep>
              <PaymentIntro>{t('components.billingSetup.modal.planOrderDescription')}</PaymentIntro>
              <BillingSetupPanel
                mode="plan-order"
                selectedPlan={toNocfoPlan(reviewPlan.id)}
                selectedBillingInterval={interval ?? 'month'}
                initialSelectedCardId="card"
                onStripeAction={handleStripeCheckout}
                isStripeActionPending={isCheckingOut}
                onManualSubmitSuccess={onUpgradeComplete}
              />
              <PaymentActionRow>
                <Button
                  type="button"
                  isSecondary
                  onClick={handleBackToReview}
                  disabled={isCheckingOut}
                >
                  {t('general.back')}
                </Button>
              </PaymentActionRow>
            </PaymentStep>
          ) : data ? (
            <PlanSelectionStep
              data={data}
              interval={interval ?? 'month'}
              onIntervalChange={nextInterval =>
                setValue('interval', nextInterval, { shouldDirty: true })
              }
              selectedPlanId={selectedPlanId || null}
              isCheckingOut={isCheckingOut}
              onSelectPlan={handleSelectPlan}
            />
          ) : null}
        </Content>
      </AnimatedContentLoader>
    </FormProvider>
  )
}

export const CustomPricingTableModal: React.FC<{
  isVisible: boolean
  handleOnClose: () => void
}> = ({ isVisible, handleOnClose }) => {
  const [t] = useTranslation()
  const [step, setStep] = useState<PricingTableStep>('plans')
  const [mountKey, setMountKey] = useState(0)

  useEffect(() => {
    if (isVisible) {
      setStep('plans')
      setMountKey(key => key + 1)
    }
  }, [isVisible])

  const handleClose = () => {
    setStep('plans')
    handleOnClose()
  }

  const header =
    step === 'payment'
      ? t('components.billingSetup.paymentCardTitle')
      : t('components.pricingTable.subscribe')

  const { width, height } = MODAL_SIZE_BY_STEP[step]

  return (
    <ModalV2
      header={header}
      isVisible={isVisible}
      handleOnClose={handleClose}
      width={width}
      height={height}
      footer={
        <ModalFooter>
          <Button onClick={handleClose}>{t('general.close')}</Button>
        </ModalFooter>
      }
    >
      <CustomPricingTable key={mountKey} onUpgradeComplete={handleClose} onStepChange={setStep} />
    </ModalV2>
  )
}

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
`

const Content = styled.div`
  width: 100%;
`

const PaymentStep = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${({ theme }) => theme.spacing.lg}rem;
  padding: ${({ theme }) => theme.spacing.md}rem;
  width: 100%;
  max-width: 36rem;
  margin: 0 auto;
`

const PaymentIntro = styled.p`
  margin: 0;
  color: ${({ theme }) => theme.colors.metalGray};
  font-size: ${({ theme }) => theme.fontSize.sm}rem;
  line-height: 1.5;
`

const PaymentActionRow = styled.div`
  display: flex;
  justify-content: flex-start;
`
